import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ChevronLeft, Save, Loader2 } from 'lucide-react';
import { ImageUpload, MultiImageUpload } from '@/components/admin/ImageUpload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { useProperty } from '@/hooks/useProperties';
import { useCreateProperty, useUpdateProperty } from '@/hooks/usePropertyMutations';
import { PROPERTY_TYPES, PROPERTY_PURPOSES, PROPERTY_STATUSES, PROPERTY_FEATURES } from '@/types/property';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';

const propertySchema = z.object({
  title: z.string().min(5, 'Título deve ter pelo menos 5 caracteres'),
  description: z.string().optional(),
  type: z.enum(['casa', 'apartamento', 'terreno', 'cobertura', 'comercial', 'rural', 'kitnet', 'sobrado']),
  purpose: z.enum(['venda', 'locacao', 'venda_locacao']),
  status: z.enum(['disponivel', 'vendido', 'alugado', 'reservado', 'inativo']),
  price: z.coerce.number().positive('Preço deve ser positivo'),
  condo_fee: z.coerce.number().min(0).optional().nullable(),
  iptu: z.coerce.number().min(0).optional().nullable(),
  city: z.string().min(2, 'Cidade é obrigatória'),
  neighborhood: z.string().min(2, 'Bairro é obrigatório'),
  street: z.string().optional(),
  number: z.string().optional(),
  cep: z.string().optional(),
  total_area: z.coerce.number().min(0).optional().nullable(),
  built_area: z.coerce.number().min(0).optional().nullable(),
  bedrooms: z.coerce.number().int().min(0).optional().nullable(),
  suites: z.coerce.number().int().min(0).optional().nullable(),
  bathrooms: z.coerce.number().int().min(0).optional().nullable(),
  parking_spots: z.coerce.number().int().min(0).optional().nullable(),
  main_image_url: z.string().optional().or(z.literal('')).or(z.literal(null)),
  video_url: z.string().url().optional().or(z.literal('')),
  gallery_images: z.array(z.string()).optional(),
  is_featured: z.boolean(),
  is_new: z.boolean(),
  features: z.array(z.string()),
});

type PropertyFormData = z.infer<typeof propertySchema>;

export default function PropertyFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = id && id !== 'novo';
  
  const { data: property, isLoading: loadingProperty } = useProperty(id || '');
  const createProperty = useCreateProperty();
  const updateProperty = useUpdateProperty();

  const form = useForm<PropertyFormData>({
    resolver: zodResolver(propertySchema),
    defaultValues: {
      title: '',
      description: '',
      type: 'casa',
      purpose: 'venda',
      status: 'disponivel',
      price: 0,
      condo_fee: null,
      iptu: null,
      city: '',
      neighborhood: '',
      street: '',
      number: '',
      cep: '',
      total_area: null,
      built_area: null,
      bedrooms: null,
      suites: null,
      bathrooms: null,
      parking_spots: null,
      main_image_url: '',
      video_url: '',
      gallery_images: [],
      is_featured: false,
      is_new: true,
      features: [],
    },
  });

  useEffect(() => {
    if (isEditing && property) {
      form.reset({
        title: property.title,
        description: property.description || '',
        type: property.type,
        purpose: property.purpose,
        status: property.status,
        price: property.price,
        condo_fee: property.condo_fee,
        iptu: property.iptu,
        city: property.city,
        neighborhood: property.neighborhood,
        street: property.street || '',
        number: property.number || '',
        cep: property.cep || '',
        total_area: property.total_area,
        built_area: property.built_area,
        bedrooms: property.bedrooms,
        suites: property.suites,
        bathrooms: property.bathrooms,
        parking_spots: property.parking_spots,
        main_image_url: property.main_image_url || '',
        video_url: property.video_url || '',
        gallery_images: property.property_images?.filter(img => !img.is_main).map(img => img.image_url) || [],
        is_featured: property.is_featured,
        is_new: property.is_new,
        features: property.features || [],
      });
    }
  }, [property, isEditing, form]);

  const onSubmit = async (data: PropertyFormData) => {
    try {
      const payload = {
        title: data.title,
        type: data.type,
        purpose: data.purpose,
        status: data.status,
        price: data.price,
        city: data.city,
        neighborhood: data.neighborhood,
        description: data.description || null,
        condo_fee: data.condo_fee,
        iptu: data.iptu,
        street: data.street || null,
        number: data.number || null,
        cep: data.cep || null,
        total_area: data.total_area,
        built_area: data.built_area,
        bedrooms: data.bedrooms,
        suites: data.suites,
        bathrooms: data.bathrooms,
        parking_spots: data.parking_spots,
        main_image_url: data.main_image_url || null,
        video_url: data.video_url || null,
        is_featured: data.is_featured,
        is_new: data.is_new,
        features: data.features,
      };

      let propertyId: string;

      if (isEditing && id) {
        await updateProperty.mutateAsync({ id, updates: payload });
        propertyId = id;
        toast.success('Imóvel atualizado com sucesso!');
      } else {
        const created = await createProperty.mutateAsync(payload);
        propertyId = created.id;
        toast.success('Imóvel criado com sucesso!');
      }

      // Save gallery images to property_images table
      const galleryImages = data.gallery_images || [];
      
      // Delete existing non-main images
      await supabase
        .from('property_images')
        .delete()
        .eq('property_id', propertyId)
        .eq('is_main', false);

      // Insert new gallery images
      if (galleryImages.length > 0) {
        const imageRecords = galleryImages.map((url, index) => ({
          property_id: propertyId,
          image_url: url,
          is_main: false,
          display_order: index,
        }));

        const { error: imgError } = await supabase
          .from('property_images')
          .insert(imageRecords);

        if (imgError) {
          console.error('Error saving gallery images:', imgError);
          toast.error('Imóvel salvo, mas houve erro ao salvar algumas imagens da galeria.');
        }
      }

      navigate('/admin/imoveis');
    } catch (error) {
      console.error('Error saving property:', error);
      toast.error('Erro ao salvar imóvel.');
    }
  };

  if (isEditing && loadingProperty) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-[600px] w-full" />
      </div>
    );
  }

  const isSubmitting = createProperty.isPending || updateProperty.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/imoveis">
            <ChevronLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {isEditing ? 'Editar Imóvel' : 'Novo Imóvel'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing ? 'Atualize as informações do imóvel' : 'Preencha as informações do novo imóvel'}
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6 md:grid-cols-2">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Título *</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Casa no Jardim Europa" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(PROPERTY_TYPES).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="purpose"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Finalidade *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(PROPERTY_PURPOSES).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(PROPERTY_STATUSES).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Descrição</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Descreva o imóvel..."
                        className="min-h-[120px]"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card>
            <CardHeader>
              <CardTitle>Valores</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6 md:grid-cols-3">
              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Preço *</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="condo_fee"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Condomínio (R$/mês)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="0" 
                        {...field} 
                        value={field.value ?? ''} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="iptu"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>IPTU (R$/ano)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="0" 
                        {...field} 
                        value={field.value ?? ''} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Location */}
          <Card>
            <CardHeader>
              <CardTitle>Localização</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6 md:grid-cols-2">
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cidade *</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Paulínia" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="neighborhood"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bairro *</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Jardim Europa" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="street"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rua</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Rua das Flores" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Número</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: 123" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cep"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CEP</FormLabel>
                    <FormControl>
                      <Input placeholder="00000-000" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Characteristics */}
          <Card>
            <CardHeader>
              <CardTitle>Características</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6 md:grid-cols-3">
              <FormField
                control={form.control}
                name="total_area"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Área Total (m²)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="0" 
                        {...field} 
                        value={field.value ?? ''} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="built_area"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Área Construída (m²)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="0" 
                        {...field} 
                        value={field.value ?? ''} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bedrooms"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quartos</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="0" 
                        {...field} 
                        value={field.value ?? ''} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="suites"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Suítes</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="0" 
                        {...field} 
                        value={field.value ?? ''} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bathrooms"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Banheiros</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="0" 
                        {...field} 
                        value={field.value ?? ''} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="parking_spots"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Vagas de Garagem</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="0" 
                        {...field} 
                        value={field.value ?? ''} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Media */}
          <Card>
            <CardHeader>
              <CardTitle>Mídia</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="main_image_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Imagem Principal</FormLabel>
                    <FormControl>
                      <ImageUpload
                        value={field.value || null}
                        onChange={(url) => field.onChange(url || '')}
                        folder="properties/main"
                        label="Imagem Principal"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="gallery_images"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Galeria de Fotos</FormLabel>
                    <FormControl>
                      <MultiImageUpload
                        value={field.value || []}
                        onChange={field.onChange}
                        folder="properties/gallery"
                        maxImages={20}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="video_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>URL do Vídeo (opcional)</FormLabel>
                    <FormControl>
                      <Input 
                        type="url" 
                        placeholder="https://youtube.com/..." 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Features */}
          <Card>
            <CardHeader>
              <CardTitle>Comodidades</CardTitle>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="features"
                render={() => (
                  <FormItem>
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                      {PROPERTY_FEATURES.map((feature) => (
                        <FormField
                          key={feature}
                          control={form.control}
                          name="features"
                          render={({ field }) => (
                            <FormItem className="flex items-center space-x-2 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(feature)}
                                  onCheckedChange={(checked) => {
                                    const value = field.value || [];
                                    if (checked) {
                                      field.onChange([...value, feature]);
                                    } else {
                                      field.onChange(value.filter((v) => v !== feature));
                                    }
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-normal">
                                {feature}
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Options */}
          <Card>
            <CardHeader>
              <CardTitle>Opções</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-8">
              <FormField
                control={form.control}
                name="is_featured"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="font-normal">Imóvel em Destaque</FormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="is_new"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2 space-y-0">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="font-normal">Marcar como Novo</FormLabel>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button variant="outline" type="button" asChild>
              <Link to="/admin/imoveis">Cancelar</Link>
            </Button>
            <Button type="submit" disabled={isSubmitting} className="gap-2">
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isEditing ? 'Atualizar' : 'Criar'} Imóvel
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
